import * as XLSX from 'xlsx'
import FileSaver from 'file-saver'

export interface XlsxSheetData {
  name: string
  headers?: string[]
  rows: (string | number | boolean | null | undefined)[][]
}

export interface XlsxExportOptions {
  filename: string
  sheets: XlsxSheetData[]
}

const DEFAULT_SHEET_NAME = 'Sheet1'
// Excel caps worksheet names at 31 characters and rejects : \ / ? * [ ] outright.
const MAX_SHEET_NAME_LENGTH = 31
const INVALID_SHEET_NAME_CHARS = /[:\\/?*[\]]/g

// Excel caps a cell at 32,767 characters, and one longer cell fails the whole workbook. Cells are
// written as given; an export whose cells can run longer fits them itself.
export const XLSX_MAX_CELL_LENGTH = 32767

const sanitizeSheetName = (name: string): string =>
  (name ?? '')
    .replace(INVALID_SHEET_NAME_CHARS, ' ')
    .trim()
    .slice(0, MAX_SHEET_NAME_LENGTH)
    .trim() || DEFAULT_SHEET_NAME

// Excel also rejects duplicate names, case-insensitively, so callers passing two
// sheets titled from the same report would otherwise fail before the download starts.
const uniquifySheetName = (name: string, used: Set<string>): string => {
  if (!used.has(name.toLowerCase())) {
    used.add(name.toLowerCase())
    return name
  }

  for (let suffixNum = 2; ; suffixNum += 1) {
    const suffix = ` (${suffixNum})`
    const candidate = `${name.slice(
      0,
      MAX_SHEET_NAME_LENGTH - suffix.length,
    )}${suffix}`

    if (!used.has(candidate.toLowerCase())) {
      used.add(candidate.toLowerCase())
      return candidate
    }
  }
}

export const generateXlsxBlob = (sheets: XlsxSheetData[]): Blob => {
  const wb = XLSX.utils.book_new()
  const usedSheetNames = new Set<string>()

  sheets.forEach(({ name, headers, rows }) => {
    const data: (string | number | boolean | null | undefined)[][] = headers
      ? [headers, ...rows]
      : rows

    const ws = XLSX.utils.aoa_to_sheet(data)
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      uniquifySheetName(sanitizeSheetName(name), usedSheetNames),
    )
  })

  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })

  return new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

export const exportToXlsx = async (
  filename: string,
  headers: string[],
  rows: (string | number | boolean | null | undefined)[][],
  sheetName = DEFAULT_SHEET_NAME,
): Promise<void> => {
  const blob = generateXlsxBlob([{ name: sheetName, headers, rows }])
  const resolvedFilename = filename.endsWith('.xlsx')
    ? filename
    : `${filename}.xlsx`
  await FileSaver.saveAs(blob, resolvedFilename)
}
